import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';
import { provideClientHydration, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { CustomUrlSerializer } from './shared/utils/url/customUrlSerializer';
import { ConfigService } from './core/config.service';
import { AuthenticationService } from './shared/services/authentication/authentication.service';
import { CONFIG_CONSTANTS, SEO_OPTIONS, SITE_ID } from './shared/constants/app.constants';
import { AuthInterceptor } from './shared/interceptors/auth-interceptor/auth-interceptor.service';
import { LocaleInterceptor } from './shared/interceptors/locale/locale-interceptor.service';
import { TranslationInterceptor } from './shared/interceptors/translation/translation-interceptor.service';
import { SiteIdInterceptor } from './shared/interceptors/site-id/site-id-interceptor.service';
import { HeaderOverrideInterceptor } from './shared/interceptors/header-overrides-interceptor/header-overrides.interceptor';
import { SsrTokensInterceptor } from './shared/interceptors/ssr-tokens/ssr-tokens-interceptor.service';
import { LAZY_MODULES } from './shared/services/lazy-loader/tokens';
import { lazyArrayToObj } from './shared/services/lazy-loader/lazy-bundles';
import { OneIdService } from './shared/services/one-id/one-id.service';
import { OneIdFactory } from './shared/services/one-id/one-id.factory';
import { WindowRef } from './shared/services/window-ref/window-ref.service';
import { DebugLogService } from './shared/services/debug-log/debug-log.service';
import { GeolocationModule } from '@wdpr/ra-angular-geolocation';
import { APPLICATION_ROUTES } from './app.routes';
import { LoggerModule, LoggerService } from '@wdpr/ra-angular-logger';
import { provideServiceWorker } from '@angular/service-worker';
import { SeoModule, SeoService } from '@wdpr/ra-angular-seo-metadata';
import { geoConfig } from './common/geo-config.constants';
import { CorrelationModule } from '@wdpr/ra-angular-correlation';
import { DeviceDetectionModule } from '@wdpr/ra-angular-device-detection';
import { ErrorHandlerModule } from '@wdpr/ra-angular-error-handler';
import { CdnModule } from '@wdpr/ra-angular-cdn';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PageKeyModule } from '@wdpr/ra-angular-page-key';
import { LocaleService } from './shared/services/locale-service/locale-service.service';
import { HeaderOverridesService } from './shared/services/header-overrides/header-overrides.service';
import { ViewportDetectionService } from './shared/services/viewport-detection/viewport-detection.service';
import { SsrService } from './shared/services/ssr/ssr.service';
import { DebugService } from './features/debug/services/debug-service/debug.service';
import { LoadingService } from './shared/services/loading-service/loading-service.service';
import { FinderAnalyticsService } from './shared/services/finder-analytics/finder-analytics.service';
import { TravelAgentService } from './shared/services/travel-agent/travel-agent.service';
import { FeatureDetectionService } from './shared/services/feature-detection/feature-detection.service';
import { FinderThemeService } from './shared/services/theme-service/finder-theme.service';
import { FinderPageKeyService } from './shared/services/finder-page-key/finder-page-key.service';
import { ContentNavigationService } from './shared/components/content-navigation/content-navigation.service';
import { RouteStateService } from './shared/services/route-state-service/route-state-service';
import { UrlParamsService } from './shared/services/url-params/url-params.service';
import { TokenInterceptor } from './shared/interceptors/token/token-interceptor.service';

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let __webpack_public_path__: string;

/**
 * https://v17.angular.io/guide/standalone-components#using-standalone-components-in-ngmodule-based-applications
 *
 * To make Interceptors work you have to include withInterceptorsFromDI in provideHttpClient
 * https://stackoverflow.com/questions/77624853/interceptor-not-intercepting-in-angular-17
 *
 * Quick Lesson on Services
 * The @Injectable property determines where in the application it will load.
 * root: Automatically loaded at the top of the application. No need to use `providers` array
 * platform: Must be manually injected with `providers` array at the starting point of the component
 *           so it will trickle down to the children
 *
 * Service notes
 *
 * LoadingService
 * This service is in charge of updating the template once the underlying component is ready to be
 * displayed. It needs to be injected once at the top of the app and DI in the `constructor` and not
 * the `provider`.
 */
export const appConfig: ApplicationConfig = {
    providers: [
        // for the INTERCEPTORS
        LocaleService,
        HeaderOverridesService,
        // For all components
        UrlParamsService,
        RouteStateService,
        FinderAnalyticsService,
        TravelAgentService,
        FeatureDetectionService,
        LoadingService,
        FinderThemeService,
        FinderPageKeyService,
        ViewportDetectionService,
        SeoService,
        SsrService,
        LoggerService,
        DebugService,
        ContentNavigationService,
        // import external modules
        importProvidersFrom(LoggerModule),
        importProvidersFrom(SeoModule), // Turns out we need this AND the SeoService
        importProvidersFrom(GeolocationModule.configure(geoConfig)),
        importProvidersFrom(CorrelationModule),
        importProvidersFrom(ErrorHandlerModule),
        importProvidersFrom(DeviceDetectionModule),
        importProvidersFrom(CdnModule),
        importProvidersFrom(BrowserAnimationsModule),
        importProvidersFrom(PageKeyModule),
        provideServiceWorker('/advanced-finder/web-workers/ngsw-worker.js', {
            //enabled: environment.production,
            enabled: false,
            scope: '/',
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideRouter(APPLICATION_ROUTES),
        provideClientHydration(
            withHttpTransferCacheOptions({
                includeHeaders: [],
                includePostRequests: false,
                includeRequestsWithAuthHeaders: false
            })
        ),
        provideHttpClient(
            withFetch(),
            // This will use the existing provided interceptors
            withInterceptorsFromDi()
        ),
        {
            provide: UrlSerializer,
            useClass: CustomUrlSerializer
        },
        {
            provide: 'CORRELATION_CONFIG',
            useFactory: (config: ConfigService) => {
                // Need to call to ensure we have loaded the config data
                config.load();

                return {
                    wdprConversationCookieName: config.getValue('convIdCookieName'),
                    wdprConversationHeader: config.getValue('convIdHeaderName'),
                    wdprCorrelationHeader: config.getValue('corrIdHeaderName'),
                    wdprEndpointFilter: '/advanced-finder/log/?'
                };
            },
            deps: [ConfigService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config: ConfigService, auth: AuthenticationService) => () =>
                config.load()
                    .then((result: any) => {
                        const baseUrl = config.getValue(CONFIG_CONSTANTS.apiUrlKey);
                        const version = config.getValue(CONFIG_CONSTANTS.defaultVersionKey);
                        const cdnList = config.getValue(CONFIG_CONSTANTS.cdnListKey);
                        const cdnPath = config.getValue(CONFIG_CONSTANTS.cdnPathKey);
                        // Set __webpack_public_path__ so the chunks js bundles
                        // for lazy loading has the correct CDN domain.
                        __webpack_public_path__ =
                            (cdnList && cdnList.length > 0 ? cdnList[0] : '') + cdnPath;
                        // ask for token early when the app initializes.
                        auth.getAuthToken(version, baseUrl).subscribe();

                        return Promise.resolve();
                    })
                    .catch((err) => err),
            deps: [ConfigService, AuthenticationService],
            multi: true,
        },
        // {
        //     provide: 'LOGGER_OPTIONS',
        //     useFactory: (config: ConfigService) => config.getLoggerConfig(),
        //     deps: [ConfigService],
        // },
        {
            provide: 'LOGASAURUS_URL',
            useValue: '/advanced-finder/log',
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LocaleInterceptor,
            multi: true,
        },
        // Per https://myjira.disney.com/browse/GIT-33664, removing SPA override for T.com
        // in prepration for Small World
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TranslationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SiteIdInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: 'SEO_OPTIONS',
            useFactory: (config: ConfigService) => {
                if (config.getSiteId() === SITE_ID.hkdl) {
                    SEO_OPTIONS.includeMetaTag.canonical = true;
                }

                return SEO_OPTIONS;
            },
            deps: [ConfigService],
        },
        {
            provide: 'PAGE_KEY_URL_ENDPOINT',
            useValue: '/advanced-finder/page-key/',
        },
        {
            provide: 'PAGE_KEY_TEMPLATE',
            useValue: [
                'siteId', 'path',
            ],
        },
        // REMOVING P13N UNTIL WE MOVE TO ANGULAR 15, DUE TO DEPENDENCIES ISSUES
        // {
        //     provide: P13N_LOADER,
        //     useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
        //         finderAnalyticsService.getGuestModel().toPromise(),
        //     deps: [FinderAnalyticsService],
        // },
        // {
        //     provide: P13N_DECISION_ATTRS_LOADER,
        //     useFactory: (finderAnalyticsService: FinderAnalyticsService) => () =>
        //         finderAnalyticsService.getDecisionAttributes(),
        //     deps: [FinderAnalyticsService],
        // },
        // {
        //     provide: P13N_BASE,
        //     useValue: {
        //         configuration: {
        //             CEA: {
        //                 decisionAttributesLoaderTimeout: 500,
        //             },
        //         },
        //     },
        // },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderOverrideInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SsrTokensInterceptor,
            multi: true
        },
        /**
         * This is how we lazy load the different pages. The Detail/Campaign/etc are processed here
         * Then loaded in src/app/shared/services/lazy-loader/lazy-loader-service.ts:lazyLoadComponent
         */
        {
            provide: LAZY_MODULES,
            useFactory: lazyArrayToObj
        },
        {
            provide: OneIdService,
            useFactory: OneIdFactory,
            deps: [WindowRef, DebugLogService, SsrService],
        }
    ]
};
